import { useEffect, useRef } from "react";
import { FaInstagram, FaLinkedin, FaSquareXTwitter } from "react-icons/fa6";
import { ParticleSphere } from "@/components/home/ParticleSphere";

interface FooterProps {
  setIsVisible?: (visible: boolean) => void;
}

export default function Footer({ setIsVisible }: FooterProps) {
  const footerRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (!setIsVisible) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      { threshold: 0.1 }
    );

    if (footerRef.current) {
      observer.observe(footerRef.current);
    }

    return () => {
      if (footerRef.current) {
        observer.unobserve(footerRef.current);
      }
    };
  }, [setIsVisible]);

  const column1Links = [
    { name: "Home", href: "/" },
    { name: "Games", href: "/games" },
    { name: "Services", href: "/services" },
    { name: "Regulatory Resources", href: "/regulatory-resources" },
    { name: "Gift Card Programs", href: "/gift-card-program" },
  ];

  const column2Links = [
    { name: "The Complete Process", href: "/complete-process" },
    { name: "Income Calculator", href: "/income-calculator" },
    { name: "Contact Us", href: "/contact-us" },
    { name: "Privacy Policy", href: "/privacy-policy" },
  ];

  return (
    <footer
      ref={footerRef}
      className="bg-brand-black relative w-full overflow-hidden text-white"
    >
      <div className="pointer-events-none absolute -right-24 -bottom-36 z-0 hidden h-[450px] w-[450px] overflow-hidden rounded-full md:block">
        <ParticleSphere />
      </div>

      {/* Main Content Area - Foreground Layer */}
      <div className="relative z-10 flex min-h-[250px] flex-col md:min-h-[435px]">
        <div className="flex w-full flex-1 items-center px-6 py-12 md:px-[100px]">
          {/* Text Content */}
          <div className="max-w-sm">
            <h2 className="font-raleway text-xl leading-tight font-bold md:text-[2rem]">
              Your Partner in Passive Income.
            </h2>
          </div>
        </div>

        {/* Red Divider Line - Full Width */}
        <div className="h-px w-full bg-red-600/50" />

        {/* Bottom Bar */}
        <div className="flex flex-col items-start justify-between gap-10 px-6 py-8 md:flex-row md:items-center md:gap-5 md:px-[100px]">
          {/* MOBILE LAYOUT (< 768px): 2 Columns */}
          <div className="block w-full md:hidden">
            <div className="flex w-full flex-row justify-between gap-4">
              {/* Column 1 */}
              <nav className="flex flex-col gap-5">
                {column1Links.map((link) => (
                  <a
                    key={link.name}
                    href={link.href}
                    className="text-sm font-light text-gray-300 transition-colors hover:text-white"
                  >
                    {link.name}
                  </a>
                ))}
              </nav>

              {/* Column 2 */}
              <div className="flex flex-col items-start gap-6 text-right">
                <nav className="flex flex-col items-start gap-5">
                  {column2Links.map((link) => (
                    <a
                      key={link.name}
                      href={link.href}
                      className="text-sm font-light text-gray-300 transition-colors hover:text-white"
                    >
                      {link.name}
                    </a>
                  ))}
                </nav>
              </div>
            </div>
          </div>

          {/* DESKTOP LAYOUT (>= 768px): Original Single Row + Side Socials */}
          <div className="hidden w-full items-center justify-between md:flex">
            <nav className="flex items-center gap-3 font-normal lg:gap-5">
              {[...column1Links, ...column2Links].map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  className="font-raleway group relative text-sm font-normal text-white"
                >
                  <span className="inline-grid">
                    <span
                      className="invisible col-start-1 row-start-1 h-0 font-semibold md:h-auto"
                      aria-hidden="true"
                    >
                      {link.name}
                    </span>
                    <span className="col-start-1 row-start-1 transition-all duration-300 group-hover:font-semibold">
                      {link.name}
                    </span>
                  </span>
                </a>
              ))}
            </nav>

            {/* Social Icons (Desktop) */}
            {/* <div className="flex items-center gap-5">
              <a
                href="#"
                className="text-white transition-colors hover:text-gray-300"
                aria-label="LinkedIn"
              >
                <FaLinkedin size={24} />
              </a>
              <a
                href="#"
                className="text-white transition-colors hover:text-gray-300"
                aria-label="Twitter"
              >
                <FaSquareXTwitter size={24} />
              </a>
              <a
                href="#"
                className="text-white transition-colors hover:text-gray-300"
                aria-label="Instagram"
              >
                <FaInstagram size={24} />
              </a>
            </div> */}
          </div>
        </div>
      </div>
    </footer>
  );
}
